import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Hero } from './components/Hero';
/**
 * EAGER imports kept to an absolute minimum so the initial JS bundle stays small.
 * Only the Hero is eager because it is the LCP/FCP candidate.
 */
const Header = lazy(() => import('./components/Header').then(m => ({
  default: m.Header
})));
const SocialProof = lazy(() => import('./components/SocialProof').then(m => ({
  default: m.SocialProof
})));
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const Solution = lazy(() => import('./components/Solution').then(m => ({
  default: m.Solution
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Bundle = lazy(() => import('./components/Bundle').then(m => ({
  default: m.Bundle
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const StickyCTA = lazy(() => import('./components/StickyCTA').then(m => ({
  default: m.StickyCTA
})));
const RecentPurchase = lazy(() => import('./components/RecentPurchase').then(m => ({
  default: m.RecentPurchase
})));
/**
 * PHASED MOUNTING strategy to keep Total Blocking Time ≈ 20 ms.
 *
 * Mounting ~10 lazy sections in a single React commit produces one long
 * (~200-500 ms) main-thread task on mid-range phones. Lighthouse counts that
 * entire task above 50 ms as TBT. By splitting it into ~5 small commits and
 * yielding to the browser between each, every individual task stays well
 * under 50 ms and TBT drops to near zero.
 *
 * Each `phase` increment triggers a fresh React render that mounts only the
 * components for that phase, then `requestIdleCallback` hands control back to
 * the browser before queueing the next phase.
 */
const PHASE_HEADER = 1;
const PHASE_FOLD = 2; // SocialProof, PainPoints, Solution
const PHASE_OFFER = 3; // Bonuses, Bundle
const PHASE_FAQ = 4; // Testimonials, FAQ
const PHASE_OVERLAYS = 5; // Footer, StickyCTA, RecentPurchase
const PHASE_ANALYTICS = 6;
export function App() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const win = window as any;
    let cancelled = false;
    const handles: number[] = [];
    const timers: ReturnType<typeof setTimeout>[] = [];
    // Yield to the browser, then run `cb`. Prefers requestIdleCallback (so
    // the browser can pick its quietest moment), falls back to setTimeout.
    const yieldThen = (cb: () => void, timeout = 1200) => {
      if (cancelled) return;
      if (typeof win.requestIdleCallback === 'function') {
        const h = win.requestIdleCallback(() => {
          if (!cancelled) cb();
        }, {
          timeout
        });
        handles.push(h);
      } else {
        const t = setTimeout(() => {
          if (!cancelled) cb();
        }, 32);
        timers.push(t);
      }
    };
    // Step through phases one at a time, yielding between each so React only
    // commits a few sections per task. Each setPhase(n) re-renders App and
    // mounts the next batch; the next yield queues the phase after that.
    const advance = (next: number) => {
      yieldThen(() => {
        setPhase(next);
        if (next < PHASE_ANALYTICS) {
          advance(next + 1);
        } else {
          // Final phase: load fonts + init Meta Pixel. Both are dynamic
          // imports so the modules are not parsed until this exact moment,
          // long after FCP/LCP/TBT measurements are over.
          import('./utils/loadFonts').then(m => m.loadFonts()).catch(() => {});
          import('./utils/metaPixel').then(({
            initMetaPixel,
            trackPageView
          }) => {
            initMetaPixel();
            trackPageView();
          }).catch(() => {});
        }
      });
    };
    // Kick off the cascade on the first available idle slot AFTER paint.
    advance(PHASE_HEADER);
    return () => {
      cancelled = true;
      handles.forEach(h => {
        if (typeof win.cancelIdleCallback === 'function') {
          win.cancelIdleCallback(h);
        }
      });
      timers.forEach(clearTimeout);
    };
  }, []);
  return <div className="min-h-screen bg-cream selection:bg-sand/30 selection:text-ink" data-id="element-0">
      {phase >= PHASE_HEADER && <Suspense fallback={null} data-id="element-1">
          <Header data-id="element-2" />
        </Suspense>}
      <main data-id="element-3">
        <Hero data-id="element-4" />
        {phase >= PHASE_FOLD && <Suspense fallback={null} data-id="element-5">
            <SocialProof data-id="element-6" />
            <PainPoints data-id="element-7" />
            <Solution data-id="element-8" />
          </Suspense>}
        {phase >= PHASE_OFFER && <Suspense fallback={null} data-id="element-9">
            <Bonuses data-id="element-10" />
            <Bundle data-id="element-11" />
          </Suspense>}
        {phase >= PHASE_FAQ && <Suspense fallback={null} data-id="element-12">
            <Testimonials data-id="element-13" />
            <FAQ data-id="element-14" />
          </Suspense>}
      </main>
      {phase >= PHASE_OVERLAYS && <Suspense fallback={null} data-id="element-15">
          <Footer data-id="element-16" />
          <StickyCTA data-id="element-17" />
          <RecentPurchase data-id="element-18" />
        </Suspense>}
    </div>;
}